import React, { useContext, useState, useEffect } from "react";
import { useSelector } from "react-redux";
import RevinitiAntTableWidget from "@/components/widgets/RevinitiAntTableWidget";
import { getCampaignBreakdownTableConfig } from "@/ts/reviniti/widget-configs";
import { campaignCreationContext } from "@ts/constants";
import { WidgetCard } from "@/components/widgets";
import { IChannel } from "@/ts/common-types";

interface IReviewRowData {
    key: string;
    clusterId: string;
    clusterName: string;
    clusterType: string;
    productsCount: number;
    budget: string;
    startDate: string;
    endDate: string;
    totalCampaigns: number;
}

interface IReviewExpandedData {
    key: string;
    id: string;
    clusterId: string;
    collectionId: string;
    campaignName: string;
    budget: string;
    campaignType: string;
    keywordType: string;
    city: string;
    timeSlot: string;
}

interface IReviewDataItem {
    rowData: IReviewRowData;
    expandedData: IReviewExpandedData[];
}

interface IReviewCampaignsTableProps {
    prepareReviewData: IReviewDataItem[];
    isLoading?: boolean;
}

const ReviewCampaignsTable: React.FC<IReviewCampaignsTableProps> = ({
    prepareReviewData,
    isLoading = false,
}) => {
    const { handleCampaignNameChange } = useContext(campaignCreationContext);
    const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);

    const clientCurrencySymbol = useSelector(
        (state: IChannel) => state.channelCurrencySymbol.value
    );

    //!Note[DND]: Expand first row by default when prepareReviewData is populated (accordion behavior)
    useEffect(() => {
        if (prepareReviewData && prepareReviewData.length > 0) {
            const firstExpandableItem = prepareReviewData.find(
                (item) => (item?.expandedData || []).length > 0
            );
            
            if (firstExpandableItem?.rowData?.key) {
                setExpandedRowKeys([firstExpandableItem.rowData.key]);
            }
        }
    }, [prepareReviewData]);

    //!Note[DND]: Custom handler to ensure accordion behavior (only one row expanded at a time)
    const handleExpandedRowsChange = (keys: React.Key[]) => {
        if (keys.length > 1) {
            const lastExpandedKey = keys[keys.length - 1];
            setExpandedRowKeys([lastExpandedKey as string]);
        } else {
            setExpandedRowKeys(keys as string[]);
        }
    };

    // Generate config once to avoid duplication (DRY principle)
    const tableConfig = getCampaignBreakdownTableConfig({
        clientCurrencySymbol,
        handleCampaignNameChange,
        expandedRowKeys,
        setExpandedRowKeys: handleExpandedRowsChange,
        expandedRowRender: (record: IReviewRowData) => {
            const dataItem = prepareReviewData.find(
                (item) => item.rowData.key === record.key
            );

            const expandedDataRows = dataItem?.expandedData || [];
            const ROW_HEIGHT = 54;
            const HEADER_HEIGHT = 47;
            const calculatedHeight = HEADER_HEIGHT + (expandedDataRows.length * ROW_HEIGHT);

            return (
                <div className="review-campaigns-expanded-table">
                    <RevinitiAntTableWidget
                        config={{
                            colDefs: tableConfig.expandedColDefs,
                            isSearchReq: false,
                            isDownloadButtonReq: false,
                            isExpandable: false,
                            pagination: false,
                            bordered: false,
                            showExpandColumn: false,
                        }}
                        widgetData={{
                            tableData: expandedDataRows,
                        }}
                        isLoading={false}
                        style={{
                            height: calculatedHeight,
                            disableScrollY: true,
                        }}
                    />
                </div>
            );
        },
        rowExpandable: (record: IReviewRowData) => {
            const dataItem = prepareReviewData.find(
                (item) => item.rowData.key === record.key
            );
            return (dataItem?.expandedData || []).length > 0;
        },
    });

    return (
        <WidgetCard bodyPaddingClass="px-0 py-0">
            <RevinitiAntTableWidget
                config={tableConfig}
                widgetData={{
                    tableData: prepareReviewData.map((item) => item.rowData),
                }}
                isLoading={false}
                style={{
                    height: "auto",
                    minHeight: "300px",
                    overflowY: "auto",
                }}
            />
        </WidgetCard>
    );
};

export default ReviewCampaignsTable;